/**
 * EventHorizonSkillEffect — Astral rift-path active: spatial movement bubble.
 *
 * Drops a 3-tile spatial bubble centered on the player. For the bubble's
 * 8-second duration, the boundary is bidirectional — nothing enters or exits:
 * enemy movement in is blocked, player movement out is blocked. The zone is
 * created in ZoneSystem with `blocksMovement: true` at boundary; movement code
 * checks `ZoneSystem.isInsideType(x, y, 'event_horizon')`.
 *
 * Phase sequence: active (8000ms) → cooldown (8000ms long tier).
 *
 * Compressed Time (event_horizon_upgrade_2): When `_compressedTimeMultiplier > 1`,
 * the skill exposes `compressedTimeAcceleration` getter returning the multiplier
 * (1.5x) while the active phase is running, 1.0 otherwise. External cooldown
 * tick code queries this per-frame to accelerate all Astral skill cooldowns.
 *
 * Singularity (event_horizon_passive): External DeathTriggerSystem hook handles
 * enemy-death explosions inside the bubble; this class exposes `activeZoneId`
 * so the passive can query zone membership.
 *
 * Upgrade inheritance (event_horizon_upgrade_1): queries resolver
 * `getAdditiveEffect('event_horizon', 'radiusTiles')` for radius bonus (+0.5).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § event_horizon
 * @see design-decisions.md § Astral Base Values
 * @see ADR-064 (attunement shared systems architecture)
 */

import { PhasedSkillEffect, type SkillPhase } from '../../PhasedSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface EventHorizonDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly zones: {
    create(config: EventHorizonZoneConfig): string;
    remove(zoneId: string): void;
  };
  readonly player: {
    getPosition(): { x: number; y: number };
  };
}

export interface EventHorizonZoneConfig {
  id: string;
  type: string;
  sourceSkillId: string;
  shape: 'circle';
  position: { x: number; y: number };
  radiusTiles: number;
  durationMs: number;
  blocksMovement: boolean;
  blocksProjectiles: boolean;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface EventHorizonConfig {
  readonly cooldownMs: number;
  readonly baseDurationMs: number;
  readonly baseRadiusTiles: number;
}

const DEFAULT_CONFIG: EventHorizonConfig = {
  cooldownMs: 8000,
  baseDurationMs: 8000,
  baseRadiusTiles: 3,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class EventHorizonSkillEffect extends PhasedSkillEffect {
  override readonly skillId = 'event_horizon';

  private readonly deps: EventHorizonDeps;
  readonly config: EventHorizonConfig;

  private _zoneId: string | null = null;
  private _resolvedRadiusTiles = 0;
  private _activationsCount = 0;

  constructor(deps: EventHorizonDeps, config: Partial<EventHorizonConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get activeZoneId(): string | null { return this._zoneId; }
  get resolvedRadiusTiles(): number { return this._resolvedRadiusTiles; }
  get activationsCount(): number { return this._activationsCount; }

  /**
   * Returns the cooldown acceleration multiplier for Compressed Time.
   * 1.5x during the active phase when upgrade is unlocked; 1.0 otherwise.
   * External cooldown code queries this to apply tree-wide acceleration.
   *
   * Queries the resolver dynamically each tick instead of baking the
   * multiplier at construction time, so runtime changes propagate.
   */
  get compressedTimeAcceleration(): number {
    if (this.phase !== 'active') return 1.0;
    return this.deps.resolver.getMultiplicativeEffect('event_horizon', 'cooldownTickMultiplier');
  }

  // --- PhasedSkillEffect implementation ---

  protected override readonly phaseSequence: readonly SkillPhase[] = ['active', 'cooldown'];

  protected override getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'active': return this.config.baseDurationMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  protected override onPhaseEnter(phase: SkillPhase): void {
    if (phase === 'active') {
      this._openBubble();
    } else if (phase === 'cooldown') {
      this._closeBubble();
    }
  }

  // --- Internal helpers ---

  private _openBubble(): void {
    const radiusBonus = this.deps.resolver.getAdditiveEffect('event_horizon', 'radiusTiles');
    this._resolvedRadiusTiles = this.config.baseRadiusTiles + radiusBonus;

    const position = this.deps.player.getPosition();
    const zoneConfig: EventHorizonZoneConfig = {
      id: `event_horizon_zone_${this._activationsCount}`,
      type: 'event_horizon',
      sourceSkillId: 'event_horizon',
      shape: 'circle',
      position: { ...position },
      radiusTiles: this._resolvedRadiusTiles,
      durationMs: this.config.baseDurationMs,
      blocksMovement: true,
      blocksProjectiles: true,
    };
    this._zoneId = this.deps.zones.create(zoneConfig);
    this._activationsCount++;
  }

  private _closeBubble(): void {
    if (this._zoneId !== null) {
      this.deps.zones.remove(this._zoneId);
      this._zoneId = null;
    }
  }

  override destroy(): void {
    this._closeBubble();
    super.destroy();
  }
}
